import type { Location, Patrol } from '@local/common-lib';
import { isUlid, locationContract } from '@local/common-lib';
import {
  ArrowBack as CancelIcon,
  Stop as FinishIcon,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import type { Theme } from '@mui/material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  InputAdornment,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { FormikProps } from 'formik';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { Contract } from 'validata';
import {
  isArray,
  isNumber,
  isString,
  maybeBoolean,
  maybeObject,
  maybeString,
} from 'validata';
import { FetchOneOfAll } from '../../../components/fetch/fetch-one.js';
import { FormikCheckList } from '../../../components/formik-fields/check-list.js';
import { FormikLocationAddressField } from '../../../components/formik-fields/location-address-field.js';
import { FormikNumberField } from '../../../components/formik-fields/number-field.js';
import { FormikSelect } from '../../../components/formik-fields/select.js';
import { FormikSlider } from '../../../components/formik-fields/slider.js';
import { FormikSwitch } from '../../../components/formik-fields/switch.js';
import { FormikTextField } from '../../../components/formik-fields/text-field.js';
import { BottomActionBar } from '../../../components/page-layout/bottom-action-bar.js';
import { CenterLayout } from '../../../components/page-layout/index.js';
import { fuelLevelMarks } from './marks.js';
import {
  guestRenderer,
  personRenderer,
  roundTimeToMinute,
} from './view-utils.js';

export interface FinishPatrolForm {
  emailEcc?: boolean;
  finished?: string;
  guestsPresent: string[]; // ids
  location?: Location;
  notes: string;
  patrollersPresent: string[]; // ids
  vehicleFinishOdometer?: number;
  vehicleFuelLevel?: number;
}

const finishTimes = (selected: string | undefined): JSX.Element[] => {
  const now = roundTimeToMinute(DateTime.local());
  const base = now.minus({ minutes: now.minute % 5 });
  const times = [
    base.minus({ minutes: 10 }),
    base.minus({ minutes: 5 }),
    base,
    base.plus({ minutes: 5 }),
    base.plus({ minutes: 10 }),
  ];
  const current = selected ? DateTime.fromISO(selected) : now;
  if (!times.some((time) => time.equals(current))) {
    times.push(current);
  } else if (!now.equals(current)) {
    times.push(now);
  }

  times.sort((a, b) => a.toMillis() - b.toMillis());
  return times.map((time) => (
    <MenuItem key={time.toISO()} value={time.toLocal().toISO()}>
      {time.toLocal().toFormat('t')}
    </MenuItem>
  ));
};

export const finishPatrolFormContractNoVehicle: Contract<
  Omit<FinishPatrolForm, 'vehicleFinishOdometer' | 'vehicleFuelLevel'>
> = {
  emailEcc: maybeBoolean(),
  finished: maybeString(),
  guestsPresent: isArray(isUlid()),
  location: maybeObject(locationContract),
  notes: isString(),
  patrollersPresent: isArray(isUlid()),
};

export const finishPatrolFormContract = (
  startOdometer: number,
): Contract<FinishPatrolForm> => ({
  ...finishPatrolFormContractNoVehicle,
  vehicleFinishOdometer: isNumber({ min: startOdometer }),
  vehicleFuelLevel: isNumber({ max: 5, min: 0 }),
});

interface ViewProps<T> {
  finishPatrolForm: FormikProps<FinishPatrolForm>;
  patrol: T;
}

export const FinishPatrolView: FC<ViewProps<Patrol.Type>> = ({
  patrol,
  finishPatrolForm,
}) => {
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const expectedPatrollers = useMemo(() => {
    const patrollers = patrol.patrollers ?? {};
    return Object.keys(patrollers)
      .filter((patrollerId) => {
        const assignment = patrollers[patrollerId];
        return (
          assignment !== undefined &&
          !assignment.declined &&
          !assignment.removed
        );
      })
      .map((patrollerId) => ({
        display: (
          <FetchOneOfAll
            dataType="person"
            id={patrollerId}
            renderFactory={personRenderer}
          />
        ),
        id: patrollerId,
      }));
  }, [patrol]);

  const expectedGuests = useMemo(() => {
    const guests = patrol.guests || {};

    return Object.keys(guests)
      .filter((guestId) => {
        const assignment = guests[guestId];
        return assignment !== undefined && !assignment.removed;
      })
      .map((guestId) => ({
        display: guestRenderer(guests[guestId]),
        id: guestId,
      }));
  }, [patrol]);

  // view
  return (
    <form
      onReset={finishPatrolForm.handleReset}
      onSubmit={finishPatrolForm.handleSubmit}
      style={{
        paddingBottom: smallScreen
          ? 'calc(152px + env(safe-area-inset-bottom, 0px))'
          : 'calc(96px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <CenterLayout>
        <Stack direction="column" spacing={3}>
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant={smallScreen ? 'h6' : 'h4'}>
                Finish Patrol
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardHeader title="Confirm People Present" />
            <CardContent>
              <Typography color="text.primary" variant="h6">
                Patrollers
              </Typography>
              <FormikCheckList
                formik={finishPatrolForm}
                id="patrollersPresent"
                items={expectedPatrollers}
              />
            </CardContent>
            {expectedGuests.length ? (
              <CardContent>
                <Typography color="text.primary" variant="h6">
                  Guests
                </Typography>
                <FormikCheckList
                  formik={finishPatrolForm}
                  id="guestsPresent"
                  items={expectedGuests}
                />
              </CardContent>
            ) : undefined}
          </Card>
          <Card>
            <CardHeader title="Vehicle" />
            {patrol.started?.vehicle ? (
              <CardContent>
                <Stack direction="column" spacing={3}>
                  <FormikNumberField
                    formik={finishPatrolForm}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">km</InputAdornment>
                      ),
                    }}
                    id="vehicleFinishOdometer"
                    label="Finish Odometer"
                    type="number"
                  />
                  <Collapse
                    in={
                      !!finishPatrolForm.values.vehicleFinishOdometer &&
                      !!patrol.started?.vehicle.startOdometer &&
                      finishPatrolForm.values.vehicleFinishOdometer >
                        patrol.started?.vehicle.startOdometer
                    }
                  >
                    <Alert severity="info">
                      Patrol distance:{' '}
                      {(
                        (finishPatrolForm.values.vehicleFinishOdometer ?? 0) -
                        patrol.started?.vehicle.startOdometer
                      ).toFixed(1)}{' '}
                      km
                    </Alert>
                  </Collapse>
                </Stack>
                <Stack direction="column" flexGrow={1} spacing={3}>
                  <Typography
                    color="text.primary"
                    id="fuel-level-slider-label"
                    variant="h6"
                  >
                    Fuel Level
                  </Typography>
                  <Box sx={{ px: 3 }}>
                    <FormikSlider
                      aria-labelledby="fuel-level-slider-label"
                      formik={finishPatrolForm}
                      id="vehicleFuelLevel"
                      marks={fuelLevelMarks(smallScreen)}
                      max={5}
                      min={0}
                      step={1}
                    />
                  </Box>
                </Stack>
              </CardContent>
            ) : (
              <CardContent>
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  A vehicle is not being used for this patrol.
                </Typography>
              </CardContent>
            )}
          </Card>
          <Card>
            <CardHeader title="Details" />
            <CardContent>
              <Stack direction={{ md: 'row', xs: 'column' }} spacing={3}>
                <Stack direction="column" flexGrow={1} spacing={3}>
                  <FormikSelect
                    formik={finishPatrolForm}
                    id="finished"
                    label="Finish Time"
                  >
                    {finishTimes(finishPatrolForm.values.finished)}
                  </FormikSelect>
                </Stack>
                <Stack direction="column" flexGrow={1} spacing={3}>
                  <FormikTextField
                    formik={finishPatrolForm}
                    id="notes"
                    label="Notes"
                    multiline
                    rows={4}
                  />
                </Stack>
              </Stack>
            </CardContent>
            <CardContent>
              <FormikLocationAddressField
                formik={finishPatrolForm}
                id="location"
                label="Location"
              />
            </CardContent>
            {patrol.eccLogOnResponse?.policeEventNumber ||
            patrol.started?.policeEventNumber ? (
              <CardContent>
                <Stack spacing={0}>
                  <FormikSwitch
                    formik={finishPatrolForm}
                    id="emailEcc"
                    label="Automatically send ECC Log Off email"
                  />
                  <Collapse in={finishPatrolForm.values.emailEcc}>
                    <Alert color="info">
                      Log Off will be automatically sent to NZ Police Emergency
                      Communications Centre (ECC).
                    </Alert>
                  </Collapse>
                  <Collapse in={!finishPatrolForm.values.emailEcc}>
                    <Alert color="warning">
                      Don't forget to Log Off with NZ Police Emergency
                      Communications Centre (ECC).
                    </Alert>
                  </Collapse>
                </Stack>
              </CardContent>
            ) : (
              <CardContent>
                <Alert color="warning">
                  Don't forget to Log Off with NZ Police Emergency
                  Communications Centre (ECC). This can be done by phone or by
                  replying to the Logon email from ECC, changing the subject to:{' '}
                  <code>
                    CPNZ - Logging Off Now{' '}
                    {patrol.started?.policeEventNumber ?? '[Event Number]'}
                  </code>
                </Alert>
              </CardContent>
            )}
          </Card>
          <BottomActionBar>
            <Stack direction={{ sm: 'row', xs: 'column' }} spacing={2}>
              <LoadingButton
                color="primary"
                fullWidth
                loading={finishPatrolForm.isSubmitting}
                onClick={finishPatrolForm.handleReset}
                startIcon={<CancelIcon />}
              >
                Oops, I haven't finished yet
              </LoadingButton>
              <LoadingButton
                color="primary"
                fullWidth
                loading={finishPatrolForm.isSubmitting}
                startIcon={<FinishIcon />}
                type="submit"
                variant="contained"
              >
                Finish Now
              </LoadingButton>
            </Stack>
          </BottomActionBar>
        </Stack>
      </CenterLayout>
    </form>
  );
};
